import { useState, useEffect, useCallback, useRef } from 'react';
import { notesAPI } from '@/lib/api';
import * as offlineDB from '@/lib/offlineDB';
import { toast } from 'sonner';

/**
 * Online/offline state tracking and background sync logic.
 */
export function useSyncActions({ setNotes }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [syncConflict, setSyncConflict] = useState(null);
  const syncInProgressRef = useRef(false);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online! Syncing changes...');
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You are offline. Changes will be saved locally.');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Sync pending changes when back online
  const syncPendingChanges = useCallback(async () => {
    if (syncInProgressRef.current) return;
    syncInProgressRef.current = true;
    try {
      const pending = await offlineDB.getPendingSync();
      if (pending.length === 0) { syncInProgressRef.current = false; return; }

      console.log(`[Sync] Syncing ${pending.length} pending changes...`);

      for (const item of pending) {
        try {
          switch (item.type) {
            case 'CREATE_NOTE':
              await notesAPI.create(item.data);
              break;
            case 'UPDATE_NOTE': {
              const localNote = await offlineDB.getNoteById(item.noteId);
              let serverNote = null;
              try {
                const serverRes = await notesAPI.get(item.noteId);
                serverNote = serverRes.data;
              } catch {
                console.log('[Sync] Note not found on server, skipping:', item.noteId);
                await offlineDB.removePendingSync(item.id);
                continue;
              }
              if (serverNote && localNote) {
                const serverUpdateTime = new Date(serverNote.updated_at);
                const pendingTime = new Date(item.timestamp);
                if (serverUpdateTime > pendingTime && serverNote.content !== localNote.content) {
                  console.log('[Sync] Conflict detected for note:', item.noteId);
                  setSyncConflict({ localNote, serverNote, noteTitle: localNote.title || serverNote.title, pendingItem: item });
                  syncInProgressRef.current = false;
                  return;
                }
              }
              await notesAPI.update(item.noteId, item.data);
              break;
            }
            case 'DELETE_NOTE':
              await notesAPI.delete(item.noteId);
              break;
            default:
              console.warn('[Sync] Unknown operation type:', item.type);
          }
          await offlineDB.removePendingSync(item.id);
        } catch (err) {
          console.error('[Sync] Failed to sync item:', item, err);
          if (err.response?.status === 404) {
            await offlineDB.removePendingSync(item.id);
          }
        }
      }

      await offlineDB.setLastSyncTime();
      setPendingSyncCount(0);
      toast.success('Changes synced successfully!');
    } catch (err) {
      console.error('[Sync] Sync failed:', err);
    }
    syncInProgressRef.current = false;
  }, []);

  // Resolve sync conflict
  const resolveSyncConflict = useCallback(async (choice) => {
    if (!syncConflict) return;
    const { localNote, serverNote, pendingItem } = syncConflict;
    try {
      switch (choice) {
        case 'local':
          await notesAPI.update(localNote.id, { title: localNote.title, content: localNote.content });
          toast.success('Local version kept');
          break;
        case 'server':
          await offlineDB.saveNote(serverNote);
          setNotes((prev) => prev.map((n) => (n.id === serverNote.id ? serverNote : n)));
          toast.success('Server version kept');
          break;
        case 'both': {
          const copyTitle = `${localNote.title || 'Untitled'} (conflict copy)`;
          await notesAPI.create({ title: copyTitle, content: localNote.content, tags: localNote.tags, notebook_id: localNote.notebook_id });
          await offlineDB.saveNote(serverNote);
          setNotes((prev) => prev.map((n) => (n.id === serverNote.id ? serverNote : n)));
          toast.success('Both versions saved');
          break;
        }
      }
      await offlineDB.removePendingSync(pendingItem.id);
      setSyncConflict(null);
      await syncPendingChanges();
    } catch (err) {
      console.error('[Sync] Failed to resolve conflict:', err);
      toast.error('Failed to resolve conflict');
    }
  }, [syncConflict, syncPendingChanges, setNotes]);

  // Sync when coming back online
  useEffect(() => {
    if (isOnline) syncPendingChanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  return {
    isOnline,
    pendingSyncCount, setPendingSyncCount,
    syncConflict, setSyncConflict,
    syncPendingChanges,
    resolveSyncConflict,
    dismissSyncConflict: () => setSyncConflict(null),
  };
}
